// Version Logs Container Component

import { useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Clock } from 'lucide-react';

import { LogEntry } from '@/features/versions/types';
import { Terminal, AnimatedSpan } from '@/components/ui';

interface VersionLogsProps {
  logs: LogEntry[];
}

export function VersionLogs({ logs }: VersionLogsProps) {
  const terminalContentRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on initial mount
  useEffect(() => {
    if (terminalContentRef.current) {
      const preElement = terminalContentRef.current.parentElement;
      if (preElement) {
        preElement.scrollTop = preElement.scrollHeight;
      }
    }
  }, []); // Empty dependency array - runs once on mount

  // Auto-scroll to bottom whenever logs change
  useEffect(() => {
    if (terminalContentRef.current) {
      const preElement = terminalContentRef.current.parentElement;
      if (preElement) {
        preElement.scrollTop = preElement.scrollHeight;
      }
    }
  }, [logs]);

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="mr-2 inline h-3 w-3 text-green-600" />;
      case 'error':
        return <XCircle className="mr-2 inline h-3 w-3 text-red-600" />;
      case 'warning':
        return (
          <AlertTriangle className="mr-2 inline h-3 w-3 text-yellow-600" />
        );
      default:
        return <Clock className="mr-2 inline h-3 w-3 text-blue-600" />;
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour12: false });
  };

  const formatDetails = (details?: Record<string, unknown>): string | null => {
    if (!details || Object.keys(details).length === 0) {
      return null;
    }

    const entries = Object.entries(details).filter(
      ([, value]) => value !== null && value !== undefined
    );

    if (entries.length === 0) {
      return null;
    }

    // Create ASCII tree structure
    const lines = entries.map(([key, value], index) => {
      const isLast = index === entries.length - 1;
      const prefix = isLast ? '└──' : '├──';

      let formattedValue: string;
      if (typeof value === 'object' && !Array.isArray(value)) {
        formattedValue = JSON.stringify(value);
      } else if (Array.isArray(value)) {
        formattedValue = `[${value.length} items]`;
      } else {
        formattedValue = String(value);
      }

      return `${prefix} ${key}: ${formattedValue}`;
    });

    return lines.join('\n');
  };

  return (
    <Terminal className="h-full">
      <div ref={terminalContentRef}>
        {logs.map((log, index) => {
          const detailsText = formatDetails(log.details);
          return (
            <AnimatedSpan key={index} delay={index * 50}>
              <span className="font-mono">
                {getLogIcon(log.type)}
                <span className="text-muted-foreground text-xs">
                  [{formatTime(log.timestamp)}]
                </span>{' '}
                <span className="text-foreground">{log.message}</span>
                {detailsText && (
                  <span className="text-muted-foreground mt-1 ml-5 block text-xs whitespace-pre">
                    {detailsText}
                  </span>
                )}
              </span>
            </AnimatedSpan>
          );
        })}
      </div>
    </Terminal>
  );
}

